import type { CampoLeido } from "./reader";
import type { DatosExtraidos, DomicilioResuelto } from "./dniExtract";

/**
 * Lo leído del documento, en la forma que espera un formulario.
 *
 * Es lo que hacía `completar` adentro del diálogo, sin pantalla, para que lo
 * use también la validación en pasos. Lo que se vuelca es lo que se VE, y lo
 * que se ve son los campos consolidados de las dos caras: el dato suelto de
 * `datos` solo cuando el consolidado no lo tiene. Antes salía de `datos`: la
 * pantalla mostraba «RODOLFO MANUEL» (probado por el código de barras) y el
 * botón escribía «RODOLFOSMANUEL», como lo leyó el MRZ. Dos caminos para el
 * mismo dato terminan diciendo cosas distintas.
 */
export interface LecturaParaValores {
  camposTotales: Record<string, CampoLeido>;
  datos: DatosExtraidos | null;
  /** El código de país del MRZ (ARG); quien lo recibe lo busca en su catálogo. */
  nacionalidadMrz?: string | null;
  domicilio: DomicilioResuelto;
  incluirDomicilio: boolean;
}

export interface ValoresLeidos {
  valores: Record<string, any>;
  /** El domicilio tal como se leyó, solo cuando NO se pudo repartir en los campos. */
  domicilioSinRepartir?: string;
}

export function valoresDeLaLectura({
  camposTotales,
  datos,
  nacionalidadMrz,
  domicilio,
  incluirDomicilio,
}: LecturaParaValores): ValoresLeidos {
  if (!datos) return { valores: {} };
  const valores: Record<string, any> = {};
  const consolidado = (campo: string) => camposTotales[campo]?.valor ?? undefined;
  const apellido = consolidado("apellido") ?? datos.lastName;
  const nombres = consolidado("nombres") ?? datos.firstName;
  const nacimiento = consolidado("fechaNacimiento") ?? datos.dateOfBirth;
  const numero = consolidado("numeroDocumento");
  if (apellido) valores.lastName = apellido;
  if (nombres) valores.firstName = nombres;
  if (nacimiento) valores.dateOfBirth = nacimiento;
  if (datos.gender) valores.gender = datos.gender;
  // El número con SU tipo: un número sin tipo no dice qué es. Si el documento
  // dio un CUIT, ese manda; si no, el número de documento.
  if (datos.taxIdType === "cuitCuil" && datos.taxId) {
    valores.taxIdType = "cuitCuil";
    valores.taxId = datos.taxId;
  } else if (numero) {
    valores.taxIdType = "du";
    valores.taxId = numero;
  } else if (datos.taxId) {
    if (datos.taxIdType) valores.taxIdType = datos.taxIdType;
    valores.taxId = datos.taxId;
  }
  if (nacionalidadMrz) valores.nationality = nacionalidadMrz;
  if (incluirDomicilio) {
    if (domicilio.streetAndNumber) valores.streetAndNumber = domicilio.streetAndNumber;
    if (domicilio.city) valores.city = domicilio.city;
    if (domicilio.country) valores.country = domicilio.country;
    if (domicilio.province) valores.province = domicilio.province;
  }
  // El texto crudo solo cuando NO se repartió: si se repartió ya está en los
  // campos y repetirlo aparte sería pedirle a la persona que lo copie dos veces.
  return {
    valores,
    domicilioSinRepartir: incluirDomicilio && domicilio.province ? undefined : datos.domicilioLeido,
  };
}
